import { useCallback, useEffect, useRef, useState } from 'react';
import {
  browserKeepsRefusal,
  kindsForRequest,
  watchCapturePermission,
  type PermissionWatch,
} from '../lib/mediaPermissionState';
import { logger } from '../services/logger';
import type { MediaError } from '../providers/types';

export interface CaptureRetry {
  /** "Try again" from the modal. */
  retry: () => void;
  /**
   * True once the browser confirms it is refusing without a dialog. A retry
   * offered here could only fail in silence, so the caller hides it: the way
   * out is the browser's own settings, which the modal already spells out.
   * Always false in Safari, which asks again on any click.
   */
  blocked: boolean;
}

/**
 * "Try again" for a capture failure, and the same retry when the customer lifts
 * the block without touching our page.
 *
 * Three rules, each from a way it went wrong:
 *
 *   - While the browser holds a block, a click does not reach `getUserMedia`.
 *     The request would be refused without a dialog, the modal would close and
 *     re-open ("it blinks"), and in Chrome every refusal only extends the block.
 *     The caller is told, and drops the offer rather than showing a dead button.
 *   - While the modal is up for a permission failure, the permission is watched.
 *     Allowing access from the address bar continues the call by itself; the
 *     customer used to sit in front of a modal that no longer applied.
 *   - One retry at a time. A click and the grant arriving together must not
 *     start two captures.
 *
 * The decision is synchronous — the watch holds the live PermissionStatus — so
 * a click still reaches `getUserMedia` inside its user gesture, which Safari
 * requires before it will ask again.
 */
export function useCaptureRetry({
  error,
  isVisible,
  onRetry,
}: {
  error: MediaError | null;
  isVisible: boolean;
  onRetry: () => Promise<void> | void;
}): CaptureRetry {
  const [blocked, setBlocked] = useState(false);
  const inFlightRef = useRef(false);
  const watchRef = useRef<PermissionWatch | null>(null);
  const onRetryRef = useRef(onRetry);
  const errorRef = useRef(error);
  useEffect(() => {
    onRetryRef.current = onRetry;
    errorRef.current = error;
  }, [onRetry, error]);

  const run = useCallback((source: 'click' | 'granted') => {
    if (inFlightRef.current) return;
    if (source === 'click' && browserKeepsRefusal() && watchRef.current?.isBlocked()) {
      logger.info('[capture-retry] access still blocked; not asking the browser');
      setBlocked(true);
      return;
    }
    inFlightRef.current = true;
    let pending: Promise<void> | void;
    try {
      pending = onRetryRef.current();
    } catch (err) {
      inFlightRef.current = false;
      throw err;
    }
    void Promise.resolve(pending).finally(() => {
      inFlightRef.current = false;
    });
  }, []);

  const watchedType = isVisible ? error?.type : undefined;
  const watchedRequest = error?.requested ?? 'both';
  const watchesPermission =
    watchedType === 'permission-denied' || watchedType === 'permission-dismissed';

  useEffect(() => {
    if (!watchesPermission) return;
    const watch = watchCapturePermission(kindsForRequest(watchedRequest), {
      onGranted: () => {
        logger.info('[capture-retry] access granted outside the page; retrying');
        run('granted');
      },
      // Safari asks again on a click whatever the status says, so a refusal
      // there is never a reason to take the offer away.
      onBlockedChange: (isBlocked) => setBlocked(isBlocked && browserKeepsRefusal()),
    });
    watchRef.current = watch;
    return () => {
      watch.cancel();
      if (watchRef.current === watch) watchRef.current = null;
    };
  }, [watchesPermission, watchedRequest, run]);

  const retry = useCallback(() => run('click'), [run]);

  return { retry, blocked: watchesPermission && blocked };
}
